'use client';

import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { InstantSearchServerState, useClearRefinements, useCurrentRefinements } from 'react-instantsearch-hooks';
import { useHits } from 'react-instantsearch-hooks-web';
import {
  appendCategoryFacetsForSelections,
  shouldAppendCategoryFacetsForPage,
} from '@/lib/tastics/product-list/categoryFacetsConfiguration';
import QuickSearch from './quickSearch';
import useMediaQuery from '@/helpers/hooks/useMediaQuery';
import ProductUpload from '../../components/productUpload';
import ViewToggleButton from '../../components/toggleView';
import { mediumDesktop, mobile, semiLargeDesktop } from '@/helpers/utils/screensizes';
import CustomStats from '@/components/atoms/project-builder/search-algolia/customstats';
import { TasticProps } from '@/lib/tastics/types/project-builder-plp';
import LocalizedIndex from '@/providers/localized-index';
import { getLocalizationInfo } from '@/project.config';
import { flattenTree } from '@/helpers/utils/flattenTree';
import usePath from '@/hooks/usePath';
import InstantSearch from '@/HOC/project-builder/InstantSearch';
import  ProductListProvider,{ useProductList } from '@/components/organisms/project-builder/project-builder-product/product-list-algolia/context';
import ProductList from '@/components/organisms/project-builder/project-builder-product/product-list-algolia';
import DesktopSortFacets from '@/components/organisms/project-builder/project-builder-product/product-list-algolia/components/desktop-facets/sort-facets';

export interface Props {
  slug?: string;
  searchQuery?: string;
  facetsConfiguration: any;
  pricesConfiguration: any;
  serverState?: InstantSearchServerState;
  setProductResults?: any;
  suggestions?: any;
  isBimLibraryPage?: any;
  viewMode?: 'grid' | 'list';
  showProjectBuilderUpload?: boolean;
  defaultFilters?: string;
  toggleView?: () => void;
}

function ProductListTastic({ noContentSuggestions, categories, setProductResults, data,
  suggestions, isBimLibraryPage, searchElement, projectForm, viewMode, defaultFilters, toggleView }: TasticProps<Props>) {
  const { updateFacetsConfiguration, updatePricesConfiguration } = useProductList();
  const { locale, slug } = useParams();
  const { hits, results } = useHits();
  const { items: currentRefinements } = useCurrentRefinements();
  const slugString = Array.isArray(slug) ? slug.join('/') : slug || '';
  const { currency } = getLocalizationInfo(locale);

  const categorySlug = (slugString as string).split('/').at(-1);
  const shouldAppendCategoryFacets = shouldAppendCategoryFacetsForPage(slugString, categorySlug);
  const localeString = Array.isArray(locale) ? locale[0] : locale;

  const { refine: clearAllRefinements } = useClearRefinements();
  const { pathWithoutQuery } = usePath();

  const selectedCategoryLabels = useMemo(() => {
    if (!shouldAppendCategoryFacets) return [];

    return currentRefinements
      .filter((item) => /categories\.(en-AU|en-NZ)\.lvl[01]/.test(item.attribute))
      .flatMap((item) => item.refinements.map((refinement) => String(refinement.label ?? refinement.value)));
  }, [currentRefinements, shouldAppendCategoryFacets]);

  const resolvedFacetsConfiguration = useMemo(() => {
    const baseFacets = data.facetsConfiguration ?? [];

    if (!shouldAppendCategoryFacets || selectedCategoryLabels.length === 0) {
      return baseFacets;
    }

    return appendCategoryFacetsForSelections(baseFacets, selectedCategoryLabels, localeString);
  }, [data.facetsConfiguration, shouldAppendCategoryFacets, selectedCategoryLabels, localeString]);

  const facetsConfiguration = useMemo<Record<string, any>>(() => {
    return resolvedFacetsConfiguration.reduce(
      (acc, configuration) => ({
        ...acc,
        [configuration.key.replace(/\{currency\}/, currency)]: {
          type: configuration.type,
          label: configuration.label,
        } as any,
      }),
      {},
    );
  }, [resolvedFacetsConfiguration, currency]);

  const pricesConfiguration = useMemo<Record<string, any>>(() => {
    return (data.pricesConfiguration ?? []).reduce(
      (acc, configuration) => ({
        ...acc,
        [configuration.key]: {
          ranges: configuration.ranges,
        } as any,
      }),
      {},
    );
  }, [data.pricesConfiguration]);

  useEffect(() => {
    updateFacetsConfiguration(facetsConfiguration);
    updatePricesConfiguration(pricesConfiguration);
  }, [facetsConfiguration, pricesConfiguration, updateFacetsConfiguration, updatePricesConfiguration]);

  useEffect(() => {
    clearAllRefinements();
  }, [clearAllRefinements, pathWithoutQuery]);

  const flattenedCategories = categories.map((category) => flattenTree(category, 'subCategories')).flat();

  useEffect(() => {
    // @ts-ignore
    setProductResults(results);
  }, [results]);

  return (
    <>
      <ProductList
        slug={categorySlug}
        searchQuery={searchElement}
        categories={flattenedCategories}
        facetsConfiguration={facetsConfiguration}
        noContentSuggestions={noContentSuggestions}
        suggestions={suggestions}
        isBimLibraryPage={isBimLibraryPage}
        projectForm={projectForm}
        viewMode={viewMode}
        defaultFilters={defaultFilters}
        toggleView={toggleView}
      />
    </>
  );
}

function ProductListAlgoliaTastic({ data, viewMode, toggleView, customerId, projectId, showProjectBuilderUpload = false, ...props }: TasticProps<Props>) {
  const [noContentSuggestions, setNoContentSuggestion] = useState<number>(0);
  const searchParams = useSearchParams();
  const searchQuery = searchParams.get('query') as string;
  const [productsResults, setProductResults] = useState<any>();
  const [productQuery, setProductQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState<any>([]);
  const isBimLibraryPage = props?.params?.slug?.[1] === "bim-library";
  const [isMediumDesktop] = useMediaQuery(mediumDesktop);
  const [issemiLargeDesktop] = useMediaQuery(semiLargeDesktop);
useEffect(() => {
  if (searchQuery) {
    setSearchTerm(searchQuery);
  }
}, [searchQuery]);
  return (
    <InstantSearch>
      <LocalizedIndex type="products">
        <ProductListProvider>
          <CustomStats setProductQuery={setProductQuery} searchQuery={searchQuery} />
          {!isMediumDesktop && showProjectBuilderUpload && (
            <div className="w-full xl:mt-[10px] md:w-auto flex items-end justify-end gap-[4px]">
              <div className="gap-[8px] items-center">
                <p className="!mb-[20px]">
                  <a
                    href="/images/files/example_spectool_upload_sku_list.csv"
                    className="font-semibold text-[16px] text-[#090909] cursor-pointer"
                    download="example_spectool_upload_sku_list.csv"
                  >
                    See Template
                  </a>
                </p>

                <ProductUpload customerId={customerId} projectId={projectId} />
              </div>
            </div>
          )}

          <div className={`flex flex-row xl:mt-[-20px] gap-4 pb-0 desktop-facets`}>
            {isMediumDesktop && <div className="lg:min-w-[280px] 2xl:min-w-[280px]"></div>}
            {showProjectBuilderUpload && (
              <div className="my-5 xl:my-0 xl:px-2 2xl:px-[72px] flex flex-col xl:py-[40px] md:flex-row items-start md:justify-between w-full gap-[4px] bg-white">
                <QuickSearch searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
              </div>
            )}
          </div>

          <ProductListTastic
            noContentSuggestions={noContentSuggestions}
            setProductResults={setProductResults}
            data={{ ...data }}
            suggestions={suggestions}
            isBimLibraryPage={isBimLibraryPage}
            searchElement={searchTerm}
            viewMode={viewMode}
            toggleView={toggleView}
            {...props}
          />
        </ProductListProvider>
      </LocalizedIndex>

    </InstantSearch>
  );
}

export default ProductListAlgoliaTastic;
